import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faEye, faTrash, faToggleOn, faToggleOff, faBook, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Table from '../../../components/Table';
import Pagination from '../../../components/Pagination';
import matrizCurricularService from '../../../services/matrizCurricularService';
import cursoService from '../../../services/cursoService';
import { useAuth } from '../../../contexts/AuthContext';

const MatrizCurricularList = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  
  const [matrizes, setMatrizes] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [filters, setFilters] = useState({
    search: '',
    idCurso: '',
    ano: '',
    ativa: ''
  });
  
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Carregar cursos para o filtro
  useEffect(() => {
    const loadCursos = async () => {
      try {
        const response = await cursoService.list({ page: 1, limit: 1000 });
        if (response.success) {
          setCursos(response.data);
        }
      } catch (err) {
        console.error('Erro ao carregar cursos:', err);
      }
    };
    loadCursos();
  }, []);

  // Carregar matrizes - apenas quando page, pageSize, idCurso ou ativa mudarem
  useEffect(() => {
    loadMatrizes();
  }, [page, pageSize, filters.idCurso, filters.ativa]);

  const loadMatrizes = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Filtrar apenas valores não vazios
      const params = {
        page,
        limit: pageSize,
      };
      
      // Adicionar filtros apenas se tiverem valor
      if (filters.search) params.search = filters.search;
      if (filters.idCurso) params.idCurso = filters.idCurso;
      if (filters.ano) params.ano = filters.ano;
      if (filters.ativa !== '') params.ativa = filters.ativa;

      const response = await matrizCurricularService.list(params);
      
      if (response.success) {
        setMatrizes(response.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalRecords(response.pagination?.totalRecords || 0);
      } else {
        setError('Erro ao carregar matrizes curriculares');
      }
    } catch (err) {
      console.error('Erro ao carregar matrizes:', err);
      setError(err.message || 'Erro ao carregar matrizes curriculares');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    
    // Aplicar imediatamente para selects (curso e status)
    if (name === 'idCurso' || name === 'ativa') {
      setPage(1);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadMatrizes();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleToggleAtiva = async (id, nomeAtual) => {
    if (!window.confirm(`Deseja realmente alterar o status desta matriz?`)) {
      return;
    }

    try {
      const response = await matrizCurricularService.toggleAtiva(id);
      
      if (response.success) {
        toast.success('Status da matriz alterado com sucesso!');
        loadMatrizes();
      } else {
        toast.error(response.message || 'Erro ao alterar status da matriz');
      }
    } catch (err) {
      console.error('Erro ao alterar status:', err);
      toast.error(err.message || 'Erro ao alterar status da matriz');
    }
  };

  const handleDelete = async (id, nome) => {
    if (!window.confirm(`Deseja realmente excluir a matriz "${nome}"?\n\nATENÇÃO: Só é possível excluir se não houver matrículas vinculadas!`)) {
      return;
    }

    try {
      const response = await matrizCurricularService.delete(id);
      
      if (response.success) {
        toast.success('Matriz excluída com sucesso!');
        loadMatrizes();
      } else {
        toast.error(response.message || 'Erro ao excluir matriz');
      }
    } catch (err) {
      console.error('Erro ao excluir:', err);
      toast.error(err.message || 'Erro ao excluir matriz');
    }
  };

  const columns = [
    { 
      accessor: 'nome', 
      Header: 'Nome', 
      sortable: true,
      Cell: (value, row) => (
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faBook} className="text-blue-500" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    { 
      accessor: 'nomeCurso', 
      Header: 'Curso', 
      sortable: true 
    },
    { 
      accessor: 'ano', 
      Header: 'Ano', 
      sortable: true,
      Cell: (value) => <span className="font-semibold">{value}</span>
    },
    { 
      accessor: 'vigenciaInicio', 
      Header: 'Vigência Início', 
      sortable: true,
      Cell: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '-'
    },
    { 
      accessor: 'vigenciaFim', 
      Header: 'Vigência Fim', 
      sortable: true,
      Cell: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '-'
    },
    { 
      accessor: 'ativa', 
      Header: 'Status', 
      sortable: true,
      Cell: (value) => (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          <FontAwesomeIcon icon={value ? faCheckCircle : faTimesCircle} />
          {value ? 'Ativa' : 'Inativa'}
        </span>
      )
    },
    {
      accessor: 'id',
      Header: 'Ações',
      Cell: (value, row) => (
        <div className="flex gap-2">
          {/* Visualizar (qualquer usuário autenticado) */}
          <button
            onClick={() => navigate(`/academico/matriz-curricular/visualizar/${value}`)}
            className="text-blue-600 hover:text-blue-800"
            title="Visualizar"
          >
            <FontAwesomeIcon icon={faEye} />
          </button>

          {/* Editar (apenas admin) */}
          {hasPermission('matriz.update') && (
            <button
              onClick={() => navigate(`/academico/matriz-curricular/editar/${value}`)}
              className="text-yellow-600 hover:text-yellow-800"
              title="Editar"
            >
              <FontAwesomeIcon icon={faPen} />
            </button>
          )}

          {/* Ativar/Desativar (apenas admin) */}
          {hasPermission('matriz.toggle_ativa') && (
            <button
              onClick={() => handleToggleAtiva(value, row.nome)}
              className={row.ativa ? 'text-green-600 hover:text-green-800' : 'text-gray-600 hover:text-gray-800'}
              title={row.ativa ? 'Desativar' : 'Ativar'}
            >
              <FontAwesomeIcon icon={row.ativa ? faToggleOn : faToggleOff} />
            </button>
          )}

          {/* Excluir (apenas admin) */}
          {hasPermission('matriz.delete') && (
            <button
              onClick={() => handleDelete(value, row.nome)}
              className="text-red-600 hover:text-red-800"
              title="Excluir"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-600">Carregando matrizes curriculares...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faBook} className="text-blue-600" />
                Matrizes Curriculares
              </h1>
              <p className="text-gray-600 mt-1">
                Gerencie as estruturas curriculares dos cursos
              </p>
            </div>
            
            {hasPermission('matriz.create') && (
              <Link
                to="/academico/matriz-curricular/novo"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} />
                Nova Matriz
              </Link>
            )}
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                onKeyPress={handleKeyPress}
                placeholder="Nome da matriz..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Curso
              </label>
              <select
                name="idCurso"
                value={filters.idCurso}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos os cursos</option>
                {cursos.map(curso => (
                  <option key={curso.idCurso} value={curso.idCurso}>{curso.nome}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ano
              </label>
              <input
                type="number"
                name="ano"
                value={filters.ano}
                onChange={handleFilterChange}
                onKeyPress={handleKeyPress}
                placeholder="Ex: 2024"
                min="2000"
                max="2100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="ativa"
                value={filters.ativa}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value="true">Ativas</option>
                <option value="false">Inativas</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                &nbsp;
              </label>
              <button
                onClick={handleSearch}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Buscar
              </button>
            </div>
          </div>
        </div>

        {/* Mensagem de Erro */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Tabela */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <Table 
            columns={columns} 
            data={matrizes}
            emptyMessage="Nenhuma matriz curricular encontrada"
          />
        </div>

        {/* Paginação */}
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={totalRecords || 0}
            itemsPerPage={pageSize}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default MatrizCurricularList;
